"use client";

/**
 * SearchingSceneOverlay - the app's kinetic searching scene, extended with
 * a determinate progress ring, shown over the streaming search (approved
 * mockup 2026-07-29).
 *
 * Why this exists: the premium SearchingScene (glowing orb, amber pulse
 * rings, aurora, rotating taglines, retailer marquee) only renders for the
 * brief loading.tsx navigation flash. The actual 30 to 90 second SSE search
 * is carried by bare skeleton cards, which read as broken. This overlay puts
 * that same premium scene over the whole streaming search and turns the
 * concentric rings into a determinate ring that counts "X of Y retailers".
 *
 * It is strictly additive and self-contained:
 *   - it opens no connection and holds no data; it is driven entirely by the
 *     progress values ComparisonStream already computes (loaded / failed /
 *     total / isDone),
 *   - it is an OPAQUE full-cover layer, so the ComparisonStream tree keeps
 *     mounting and streaming underneath undisturbed (the no-remount
 *     invariant is preserved); it simply hides the skeletons until the
 *     results are ready,
 *   - it fades out and unmounts the instant the stream completes, revealing
 *     the already-loaded comparison.
 *
 * The visual language is a faithful copy of SearchingScene (same taglines,
 * actions, retailers, ring pulse, aurora) so the loading.tsx -> streaming
 * handoff is seamless. SearchingScene itself is NOT modified. Gated behind
 * SEARCH_SCENE_ENABLED so OFF = today, byte for byte.
 *
 * Reduced motion: rings, aurora, marquee and copy rotation freeze; a static
 * representative frame is shown.
 */

import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { SearchAgainBar } from "./SearchAgainBar";

// useLayoutEffect on the client (runs before paint), useEffect on the server
// (no-op, avoids the SSR warning).
const useIsoLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

// The indeterminate spin duration, in ms - must match `sso-spin 1.5s` below.
const SPIN_MS = 1500;

// B-132 (owner-designated, locked 2026-07-31): the searching scene tells
// Octopusden's value story while the search runs. Each line maps to a REAL
// True Final Price layer / search step, so nothing here is a false claim:
//   2 -> per-retailer stock check (same stock_status the "N have it in stock"
//        comparison header reads)      6 -> delivery-to-postcode layer
//   3 -> on-sale discount layer        7 -> return-policy + grey-import + ACL
//   4 -> cashback + loyalty layers     8 -> TFP computation + ranking
//   5 -> cashback + club deals (B-029, 2 Oct: no rebate data, owner-approved
//        words)                        9 -> assembling the comparison page
// Deliberately NO "negotiate" line: the search does not negotiate; that is the
// separate post-comparison "Get my best price" action (owner-confirmed).
const TAGLINES = [
  "Hunting your best deal",
  "Finding who has it in stock",
  "Checking every sale and discount",
  "Stacking cashback and loyalty",
  "Checking cashback and club deals",
  "Pricing in delivery",
  "Checking returns and warranty",
  "Working out your True Final Price",
  "Building your comparison",
] as const;

// r=78 ring geometry, matching the approved mockup.
const RING_R = 78;
const RING_C = 2 * Math.PI * RING_R; // ~490.09

interface Props {
  /** Retailers that returned product data successfully. */
  loaded: number;
  /** Retailers that errored / were excluded by the validator. */
  failed: number;
  /** Total retailers being checked. 0 before the `targets` event lands. */
  total: number;
  /** True once the stream completes (or fails). Triggers fade-out. */
  isDone: boolean;
  /**
   * Walk-away actions (2026-08). ONLY the live ComparisonStream passes this;
   * the sub-second handoff surfaces (RedirectToSaved, StaleCeilingRefresh)
   * that also mount this overlay leave it undefined, so no buttons appear
   * there. Rendered only while the search is still running (!isDone), so it
   * disappears the moment results land.
   */
  walkAway?: {
    onBrowse: () => void;
    onStartAnother: () => void;
    /** Background the current search, then start the typed query. */
    onSearchAnother: (query: string) => void;
    /** True for an anonymous shopper (shows the "email me when ready" field). */
    isAnonymous: boolean;
    /** Store the address + attach it to the search (does NOT background it). */
    onCaptureEmail: (email: string) => void;
  };
}

function EmailCapture({
  onSubmit,
}: {
  onSubmit: (email: string) => void;
}) {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const valid = email.trim().includes("@");

  if (sent) {
    return (
      <p className="text-center text-[13px] text-muted-foreground">
        Thanks. We&apos;ll email you the moment it&apos;s ready.
      </p>
    );
  }
  // Twins the search field EXACTLY: reuses the same bar/pill/icon/submit
  // classes (signed-off Option C1, 2026-08-15), so the two rows are a single
  // consistent field style - flat muted pill + amber square submit. The submit
  // is disabled (light amber, :disabled{opacity:.4}) until a valid email, then
  // turns solid amber - the same active-state signal as the search submit.
  return (
    <div className="flex w-full flex-col items-stretch gap-2">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const value = email.trim();
          if (!value.includes("@")) return;
          onSubmit(value);
          setSent(true);
        }}
        className="mio-search-again-bar w-full"
        aria-label="Get notified when ready"
      >
        <div className="mio-search-again-pill">
          <svg
            className="mio-search-again-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.8}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="3" y="5" width="18" height="14" rx="2" />
            <path d="M4 7.5l8 5.5 8-5.5" />
          </svg>
          <input
            type="email"
            autoComplete="email"
            maxLength={320}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Your email address"
            aria-label="Your email"
            style={{ background: "transparent", width: "100%" }}
          />
        </div>
        <button
          type="submit"
          className="mio-search-again-submit"
          disabled={!valid}
          aria-label="Notify me when ready"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.4}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <line x1="5" y1="12" x2="19" y2="12" />
            <polyline points="12 5 19 12 12 19" />
          </svg>
        </button>
      </form>
      <p className="text-center text-[12px] text-muted-foreground">
        We&apos;ll email you the moment it&apos;s ready.
      </p>
    </div>
  );
}

export function SearchingSceneOverlay({
  loaded,
  failed,
  total,
  isDone,
  walkAway,
}: Props) {
  // If the stream is already done at first mount (e.g. an instant cache
  // hit), never show the scene - avoids a jarring one-frame flash.
  const [removed, setRemoved] = useState(isDone);
  const [tagIdx, setTagIdx] = useState(0);
  const reduce = useRef(false);
  const spinRef = useRef<SVGSVGElement>(null);

  // Phase-sync the indeterminate spin to the wall clock so that separate
  // overlay INSTANCES never jump backwards at a handoff. A search that resolves
  // to a saved page hands off through three overlays in ~1s (/compare loading
  // -> RedirectToSaved -> /c loading); each new instance's CSS animation would
  // otherwise restart at its base angle (-90deg), snapping the ring back to the
  // start - the "forward and back" the user saw. By setting animation-delay to
  // -(now mod duration), every instance is at the SAME angle at the same
  // moment, so a handoff lands on the identical angle and the ring keeps
  // rotating smoothly. Applied before paint (layout effect) so there is no
  // first-frame flash. No-op once the total is known (the spin is replaced by
  // the determinate progress arc).
  useIsoLayoutEffect(() => {
    const el = spinRef.current;
    if (el && total === 0) {
      el.style.animationDelay = `${-(Date.now() % SPIN_MS)}ms`;
    }
  }, [total]);

  // Detect reduced motion once on mount. The scene is OPAQUE from the first
  // frame (no entry fade-in). Why: loading.tsx and ComparisonStream each mount
  // their own overlay; the previous entry fade-in left the NEW instance at
  // opacity 0 for a frame at the handoff, so the page skeletons flashed
  // through it - the "blink". Rendering opaque immediately covers the handoff
  // seamlessly. The fade-OUT on completion is kept (see the isDone effect).
  useEffect(() => {
    reduce.current =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
  }, []);

  // Rotate the single tagline (ambient copy), skipped under reduced motion.
  // This ONLY advances the index; AnimatePresence (mode="wait") owns the
  // crossfade. The previous version hand-rolled the fade with a setTimeout that
  // swapped the text 300ms into a 320ms CSS fade - fine when idle, but during
  // the live search the main thread is busy streaming retailer updates, so that
  // timer could fire late and the old line was still visible when the new one
  // appeared (the "overlapping messages" bug). mode="wait" fully exits the old
  // line before mounting the new one, so two can never show at once.
  useEffect(() => {
    if (isDone || reduce.current) return;
    const t = setInterval(() => {
      setTagIdx((i) => (i + 1) % TAGLINES.length);
    }, 3800);
    return () => clearInterval(t);
  }, [isDone]);

  // On completion, fade out then unmount to reveal the loaded results.
  useEffect(() => {
    if (!isDone) return;
    const id = setTimeout(() => setRemoved(true), 480);
    return () => clearTimeout(id);
  }, [isDone]);

  if (removed) return null;

  const done = loaded + failed;
  const knownTotal = total > 0;
  const dashoffset = knownTotal
    ? RING_C * (1 - Math.min(1, done / total))
    : RING_C * 0.72; // indeterminate: a fixed arc that spins
  // Opaque from the first frame; only fades out when the stream completes.
  const visible = !isDone;

  return (
    <div
      className="fixed inset-0 z-[55] overflow-y-auto bg-background"
      style={{ opacity: visible ? 1 : 0, transition: "opacity 450ms ease" }}
      role="status"
      aria-live="polite"
      aria-label={
        knownTotal
          ? `Searching, ${done} of ${total} retailers checked`
          : "Searching Australian retailers"
      }
    >
      {/* aurora glow - fixed to the viewport so it never scrolls with content */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 -z-10 flex items-center justify-center"
      >
        <div
          className="sso-aurora absolute h-[80vh] w-[80vh] max-w-[640px] max-h-[640px] rounded-full opacity-30 blur-3xl"
          style={{
            background:
              "radial-gradient(circle at center, hsl(var(--accent) / 0.55) 0%, transparent 65%)",
          }}
        />
      </div>

      {/* Centering wrapper: min-h-full keeps the scene vertically centered when
          it fits, and lets it grow + SCROLL (via the parent's overflow-y-auto)
          when the content is taller than the viewport - instead of the flex
          column shrinking its children, which used to compress the orb box and
          spill the fixed-size ring SVG over the text below. */}
      <div className="relative flex min-h-full flex-col items-center justify-center px-5 py-16">
      {/* orb stage: the determinate progress ring (grows as retailers resolve)
          with the live retailer COUNT restored in its centre (owner request,
          2026-08-17). The 4623b1e refine had dropped the number to a "quiet"
          arc; the count is back so "N of M retailers" reads as it works. Pulse
          rings + "Live" label stay removed. 128px ring (was 190px). */}
      <div
        className="relative mb-8 flex h-[128px] w-[128px] shrink-0 items-center justify-center"
        role="img"
        aria-label="Searching"
      >
        <svg
          ref={spinRef}
          width="128"
          height="128"
          viewBox="0 0 190 190"
          className="absolute inset-0"
          style={{
            transform: "rotate(-90deg)",
            animation:
              !knownTotal && !reduce.current
                ? "sso-spin 1.5s linear infinite"
                : undefined,
          }}
        >
          <circle cx="95" cy="95" r={RING_R} fill="none" stroke="hsl(var(--border))" strokeWidth="6" />
          <circle
            cx="95"
            cy="95"
            r={RING_R}
            fill="none"
            stroke="url(#sso-grad)"
            strokeWidth="6"
            strokeLinecap="round"
            strokeDasharray={RING_C.toFixed(2)}
            strokeDashoffset={dashoffset.toFixed(2)}
            style={{ transition: "stroke-dashoffset 650ms cubic-bezier(.3,.7,.3,1)" }}
          />
          <defs>
            <linearGradient id="sso-grad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor="#fde68a" />
              <stop offset="1" stopColor="#f6c453" />
            </linearGradient>
          </defs>
        </svg>

        {/* 2026-08-17 (owner request): the live retailer count INSIDE the ring,
            restored. The 4623b1e refine had reduced the ring to a "quiet" arc
            with no number; the count reads "<checked> of <total> retailers" so
            it is visible that the search is actually working. Uses done/total,
            already computed above - purely a display element. */}
        {knownTotal ? (
          <div
            aria-hidden="true"
            className="relative flex flex-col items-center justify-center"
          >
            <span className="font-mono text-[28px] font-semibold leading-none tabular-nums text-foreground">
              {done}
            </span>
            <span className="mt-1 text-[9px] uppercase tracking-[0.14em] text-muted-foreground">
              of {total} retailers
            </span>
          </div>
        ) : null}
      </div>

      {/* Fixed height reserves the TALLEST tagline at each breakpoint (mobile
          taglines wrap to at most 2 lines = 72px, measured across 320-414px;
          desktop is always 1 line), so rotating copy never changes the block
          height and the vertically-centred scene never jumps. overflow-hidden
          is a safety clip. AnimatePresence mode="wait" fully fades the OLD line
          out before the NEW one mounts, so two messages can never overlap - the
          bug the old setTimeout crossfade hit under main-thread load. */}
      <div className="mb-7 flex min-h-[4.5rem] items-center justify-center overflow-hidden sm:min-h-[3.5rem]">
        <AnimatePresence mode="wait" initial={false}>
          <motion.h1
            key={tagIdx}
            className="text-center font-sans text-3xl font-medium tracking-tight text-foreground sm:text-5xl"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.26, ease: "easeInOut" }}
          >
            {TAGLINES[tagIdx]}
          </motion.h1>
        </AnimatePresence>
      </div>

      <p className="mb-10 text-center text-[12px] uppercase tracking-[0.25em] text-muted-foreground">
        This usually takes 30 to 60 seconds
      </p>

      {walkAway && !isDone ? (
        <div className="flex w-full max-w-[360px] flex-col items-stretch gap-3">
          {/* Search another: the SearchAgainBar's white card is flattened here
              (sso-flat-field, see the <style> below) so it becomes a plain pill
              that TWINS the email field - one consistent field style (signed-
              off Option C1). Backgrounds the CURRENT search, then starts the
              typed one. */}
          <div className="sso-flat-field">
            <SearchAgainBar
              initialQuery=""
              placeholder="Search any product..."
              onSubmitQuery={walkAway.onSearchAnother}
            />
          </div>
          {/* Anonymous shoppers keep the email capture: their reliable ready
              notification (no account to email). Signed-in users are notified
              automatically, so they get only the search + Browse actions. */}
          {walkAway.isAnonymous ? (
            <EmailCapture onSubmit={walkAway.onCaptureEmail} />
          ) : null}
          {/* Solid amber, always active, no arrow (owner request 2026-08-15):
              matches the active submit CTA exactly - same gradient, text colour
              and glow as .mio-search-again-submit. */}
          <button
            type="button"
            onClick={walkAway.onBrowse}
            className="mt-1 w-full rounded-full px-5 py-3 text-[14px] font-semibold transition hover:brightness-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            style={{
              background: "linear-gradient(180deg, #f6c453 0%, #e9b143 100%)",
              color: "#1a1306",
              boxShadow:
                "inset 0 1px 0 rgba(255, 255, 255, 0.35), inset 0 -1px 0 rgba(0, 0, 0, 0.18), 0 10px 24px -12px rgba(246, 196, 83, 0.55)",
            }}
          >
            Browse comparisons
          </button>
        </div>
      ) : null}
      </div>

      <style>{`
        /* Scene-scoped ONLY: flatten the SearchAgainBar's white card so the
           search field becomes a plain pill that twins the email field. Scoped
           to .sso-flat-field, so /compare, /c, /browse keep their card. */
        .sso-flat-field .mio-search-again-card {
          background: transparent;
          border: none;
          box-shadow: none;
          padding: 0;
          margin: 0;
          -webkit-backdrop-filter: none;
          backdrop-filter: none;
        }
        .sso-aurora { animation: sso-aurora-pulse 5s infinite ease-in-out; }
        @keyframes sso-aurora-pulse {
          0%, 100% { opacity: 0.22; transform: scale(0.95); }
          50%      { opacity: 0.42; transform: scale(1.08); }
        }
        @keyframes sso-spin { to { transform: rotate(270deg); } }
        @media (prefers-reduced-motion: reduce) {
          .sso-aurora { animation: none; opacity: 0.3; }
        }
      `}</style>
    </div>
  );
}
